import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { AppDispatch } from '../../../app/store'
import { FiChevronDown, FiChevronUp } from 'react-icons/fi'
import { useMeasure } from 'react-use'
import {
  openBackfillModal,
  openImportOrdersModal,
  openSettingsModal,
  setCurrentChannel,
  setCurrentChannelAccount,
  openInkfrogCsvModal,
  openInkfrogOrdersCsvModal,
  expandInkfrogOrdersRecentImports,
  collapseInkfrogOrdersRecentImports,
} from '../integrationsSlice'
import ConnectButton from './ConnectButton'
import { fetchInkfrogOrdersImportBackfill } from '../../../actions/backFillActions'
import { selectRootAccounts, selectRootAdmin, selectRootUser } from '../../../rootSlice'
import ChannelAccountTable from './ChannelAccountTable'
import { useConnectionClick } from './hooks'
import ChannelKeysTable from './ChannelKeysTable'
import McpKeyDisplay from './McpKeyDisplay'
import FeedConfigTable from './FeedConfigTable'
import { isTeamOwner as checkIsTeamOwner } from '../isTeamOwner'

export default function IntegrationCard({
  integration,
  ownerId,
  sharedAccountIds = [],
  csvModalVisible,
  onLimitExceeded,
  retrieveAccounts,
  /** Disconnect/remove any team member's connection (owner/admin or My Team permission). Everyone may still backfill. */
  canDisconnectAllTeamIntegrations = false,
  /** Used with shared users: allow disconnect on rows they connected (email/owner match) when lacking permission above */
  currentUserEmail = '',
}) {
  const dispatch = useDispatch<AppDispatch>()
  const [detailsRef, { height: detailsHeight }] = useMeasure()
  const user = useSelector(selectRootUser)
  const isAdmin = useSelector(selectRootAdmin)
  const rootAccounts = useSelector(selectRootAccounts)
  const isTeamOwner = checkIsTeamOwner(user)
  const accounts = rootAccounts.map((_account) => ({ ..._account, isOwner: user.email === _account.owner }))
  const {
    id: channelId,
    channel,
    title,
    description,
    logo,
    enabled: channelEnabled,
    backfillEnabled,
    settingsEnabled,
    connectDisabled,
    type,
    ppKeys,
    connected,
    customBanner,
    uninstallEnabled,
    pkeyTableEnabled,
    ownerEnabledOnly,
    accounts: integrationAccounts,
    inkfrogListingRowCount,
    inkfrogListingImportCount,
    inkfrogListingByEbayUser,
    inkfrogOrderImportCount,
    inkfrogOrderRecentImports,
    inkfrogOrdersRecentImportsLimit,
    inkfrogOrderImportsRetentionNote,
    gmcSetupIncomplete,
  } = integration

  // Prefer the per-integration accounts computed in `useIntegrations` (it merges
  // the session-cached `rootAccounts` with rows synthesized from
  // `integrations_keys` tags so OAuth-redirect-only flows like GA / GTM still
  // populate). Fall back to filtering rootAccounts ourselves for safety.
  const channelAccounts = (Array.isArray(integrationAccounts) && integrationAccounts.length > 0
    ? integrationAccounts
    : accounts.filter((_account) => _account.channel === channel)
  ).map((_account) => ({
    ..._account,
    isOwner:
      _account.isOwner === true || (_account.owner && user.email === _account.owner) || false,
  }))

  /**
   *
   * Handle description height
   *
   */
  const MAX_DETAILS_HEIGHT = 80
  const [expandButtonVisible, setExpandButtonVisible] = useState(false)
  const [expanded, toggleExpand] = useState(false)
  const handleExpand = () => {
    toggleExpand(!expanded)
  }

  useEffect(() => {
    if (!detailsHeight) return

    if (detailsHeight > MAX_DETAILS_HEIGHT) {
      setExpandButtonVisible(true)
    }
  }, [detailsHeight])

  const cardHeightClassName = expanded ? 'tw-h-full' : 'tw-h-[240px]'
  const detailsHeightClassName = expanded ? 'tw-h-full' : `tw-h-[80px]` // make sure this height is the same as MAX_DETAILS_HEIGHT

  /**
   *
   * Handle connect click
   *
   */
  const onConnectClick = useConnectionClick({ user, integration, onLimitExceeded })

  const onSettingsClick = () => {
    dispatch(setCurrentChannel(integration))
    dispatch(openSettingsModal(undefined))
  }
  const onBackfillClick = () => {
    if (channelAccounts.length < 1) return
    dispatch(setCurrentChannel(integration))
    dispatch(setCurrentChannelAccount(channelAccounts[0]))
    dispatch(openBackfillModal(undefined))
  }
  const onBackfillForAccount = (account: any) => {
    dispatch(setCurrentChannel(integration))
    dispatch(setCurrentChannelAccount(account))
    dispatch(openBackfillModal(undefined))
  }
  const showBackfillOption = connected && backfillEnabled && channelAccounts.length >= 1
  const onImportOrdersClick = () => {
    dispatch(setCurrentChannel(integration))
    dispatch(openImportOrdersModal())
  }
  const showImportOrdersOption = connected && channelId === 'ebay' && channelAccounts.length >= 1
  const onConfigureClick = settingsEnabled ? onSettingsClick : onConnectClick

  const inkfrogUploadMenu =
    channelId === 'inkfrog_listings' && !uninstallEnabled
      ? {
          onListings: () => dispatch(openInkfrogCsvModal(undefined)),
          onOrders: () => dispatch(openInkfrogOrdersCsvModal(undefined)),
        }
      : null

  const orderRecentImports = Array.isArray(inkfrogOrderRecentImports) ? inkfrogOrderRecentImports : []

  return (
    <div className="tw-col-span-1 tw-min-w-0">
      <div
        className={`${cardHeightClassName} tw-flex tw-flex-col tw-rounded-lg tw-shadow-md tw-p-4 tw-bg-white hover:tw-ring hover:tw-ring-blue-600`}
      >
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-6 tw-shrink-0">
          <div
            className={`${channelId === 'openai_ads' ? 'tw-w-auto' : 'tw-w-24'} tw-h-12 tw-flex tw-items-center tw-justify-start`}
          >
            {logo}
          </div>
          {connected ? (
            <div className="tw-flex tw-items-center tw-space-x-2">
              {channelId === 'amazon-ads' && !connectDisabled ? (
                <button
                  type="button"
                  onClick={onConnectClick}
                  className="tw-text-sm tw-cursor-pointer tw-rounded-md tw-border tw-border-slate-300 hover:tw-border-blue-600 hover:tw-bg-blue-600 hover:tw-text-white tw-transition-colors tw-duration-150 tw-shadow-sm tw-px-2.5 tw-py-1"
                >
                  Connect another account
                </button>
              ) : null}
              <ConnectButton
                connected
                channelId={channelId}
                onClick={onConnectClick}
                canConnect={!connectDisabled}
                uninstallEnabled={uninstallEnabled}
                integration={integration}
                onConfigureClick={onConfigureClick}
                onBackfillClick={onBackfillClick}
                showBackfillOption={showBackfillOption}
                onImportOrdersClick={onImportOrdersClick}
                showImportOrdersOption={showImportOrdersOption}
                inkfrogDualUploadDisconnected={inkfrogUploadMenu ?? undefined}
              />
            </div>
          ) : (
            <ConnectButton
              onClick={onConnectClick}
              channelId={channelId}
              connected={false}
              inkfrogDualUploadDisconnected={inkfrogUploadMenu ?? undefined}
            />
          )}
        </div>

        <div className="tw-flex tw-flex-col tw-min-h-0 tw-flex-1">
          <div className="tw-text-base tw-font-bold tw-mb-1 tw-shrink-0">
            {connected
              ? channelId === 'inkfrog_listings'
                ? 'Your Inkfrog CSV imports'
                : `Your ${title} Accounts`
              : title}
          </div>
          <div className={`${detailsHeightClassName} tw-min-h-0 tw-overflow-hidden tw-text-sm tw-text-slate-500`}>
            <div ref={detailsRef}>
              {connected ? (
                <div>
                  {gmcSetupIncomplete ? (
                    <button
                      type="button"
                      onClick={onConnectClick}
                      className="tw-mb-3 tw-w-full tw-rounded-lg tw-border tw-border-amber-200 tw-bg-amber-50 tw-px-3 tw-py-2 tw-text-left tw-text-sm tw-text-amber-900 hover:tw-bg-amber-100 tw-transition-colors"
                    >
                      <span className="tw-font-semibold">Setup incomplete.</span>{' '}
                      Select your Merchant Center account to finish connecting.
                    </button>
                  ) : null}
                  {channelId === 'inkfrog_listings' ? (
                    <div className="tw-space-y-4 tw-text-slate-600">
                      <div>
                        <p className="tw-mb-1 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500">
                          Listings import
                        </p>
                        <div className="tw-rounded-lg tw-border tw-border-slate-200 tw-w-full tw-text-sm tw-shadow-sm tw-max-h-40 tw-overflow-y-auto">
                          <table className="tw-table-auto tw-border-collapse tw-w-full">
                            <thead className="tw-sticky tw-top-0 tw-z-[1] tw-border-b tw-font-medium tw-text-slate-500 tw-bg-slate-50 tw-text-left">
                              <tr>
                                <th className="tw-px-2 tw-py-1.5">eBay user</th>
                                <th className="tw-px-2 tw-py-1.5 tw-text-right">Listing rows</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(Array.isArray(inkfrogListingByEbayUser) ? inkfrogListingByEbayUser : []).map((row, i) => (
                                <tr
                                  key={`${row.ebayUser ?? '∅'}-${i}`}
                                  className={i !== 0 ? 'tw-border-t tw-border-slate-200' : ''}
                                >
                                  <td className="tw-px-2 tw-py-1.5 tw-text-slate-700 tw-break-all">
                                    {row.ebayUser != null && String(row.ebayUser).trim() !== ''
                                      ? row.ebayUser
                                      : `not available`}
                                  </td>
                                  <td className="tw-px-2 tw-py-1.5 tw-text-right tw-tabular-nums tw-font-medium tw-text-slate-800">
                                    {row.listingCount}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <p className="tw-mt-1 tw-text-xs tw-text-slate-500">
                          <span className="tw-font-medium tw-text-slate-700">{inkfrogListingRowCount ?? 0}</span>{' '}
                          listing row
                          {(inkfrogListingRowCount ?? 0) === 1 ? '' : 's'} · {(inkfrogListingImportCount ?? 0) > 0 ? (
                            <>
                              {inkfrogListingImportCount} CSV upload{inkfrogListingImportCount === 1 ? '' : 's'}
                            </>
                          ) : (
                            'no uploads recorded'
                          )}
                          . Re-uploads update by Inkfrog ID or SKU. Use Upload above to add a listings CSV.
                        </p>
                      </div>

                      <div className="tw-border-t tw-border-slate-200 tw-pt-3">
                        <p className="tw-mb-1 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500">
                          Recent order CSV uploads
                        </p>
                        <div className="tw-rounded-lg tw-border tw-border-slate-200 tw-w-full tw-text-sm tw-shadow-sm tw-max-h-48 tw-overflow-y-auto">
                          <table className="tw-table-auto tw-border-collapse tw-w-full">
                            <thead className="tw-sticky tw-top-0 tw-z-[1] tw-border-b tw-font-medium tw-text-slate-500 tw-bg-slate-50 tw-text-left">
                              <tr>
                                <th className="tw-px-2 tw-py-1.5">Uploaded</th>
                                <th className="tw-px-2 tw-py-1.5">File</th>
                                <th className="tw-px-2 tw-py-1.5">Channel</th>
                                <th className="tw-px-2 tw-py-1.5 tw-text-right">Rows</th>
                                <th className="tw-px-2 tw-py-1.5 tw-text-right"> </th>
                              </tr>
                            </thead>
                            <tbody>
                              {orderRecentImports.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan={5}
                                    className="tw-px-2 tw-py-2 tw-text-center tw-text-slate-500 tw-text-xs"
                                  >
                                    No uploads yet
                                  </td>
                                </tr>
                              ) : (
                                orderRecentImports.map((imp, i) => {
                                  const uploadedLabel =
                                    imp.createdAt && !Number.isNaN(Date.parse(imp.createdAt))
                                      ? new Date(imp.createdAt).toLocaleString(undefined, {
                                          dateStyle: 'short',
                                          timeStyle: 'short',
                                        })
                                      : '—'
                                  const ch =
                                    imp.channel === 'amazon' ? 'Amazon' : imp.channel === 'ebay' ? 'eBay' : imp.channel
                                  return (
                                    <tr
                                      key={imp.id || i}
                                      className={i !== 0 ? 'tw-border-t tw-border-slate-200' : ''}
                                    >
                                      <td className="tw-px-2 tw-py-1.5 tw-text-slate-700 tw-whitespace-nowrap">
                                        {uploadedLabel}
                                      </td>
                                      <td
                                        className="tw-px-2 tw-py-1.5 tw-text-slate-700 tw-max-w-[120px] tw-truncate"
                                        title={imp.originalFilename || undefined}
                                      >
                                        {imp.originalFilename && String(imp.originalFilename).trim()
                                          ? imp.originalFilename
                                          : '—'}
                                      </td>
                                      <td className="tw-px-2 tw-py-1.5 tw-text-slate-700">{ch}</td>
                                      <td className="tw-px-2 tw-py-1.5 tw-text-right tw-tabular-nums tw-font-medium tw-text-slate-800">
                                        {imp.rowCount}
                                      </td>
                                      <td className="tw-px-2 tw-py-1.5 tw-text-right">
                                        <button
                                          type="button"
                                          className="tw-rounded-md tw-border tw-border-slate-300 tw-bg-white tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-slate-800 tw-shadow-sm hover:tw-border-blue-600 hover:tw-bg-blue-600 hover:tw-text-white tw-transition-colors"
                                          onClick={() =>
                                            dispatch(fetchInkfrogOrdersImportBackfill({ import_id: imp.id }))
                                          }
                                        >
                                          Backfill
                                        </button>
                                      </td>
                                    </tr>
                                  )
                                })
                              )}
                            </tbody>
                          </table>
                        </div>
                        {(inkfrogOrderImportCount ?? 0) > 10 || (inkfrogOrdersRecentImportsLimit ?? 10) > 10 ? (
                          <div className="tw-mt-1 tw-flex tw-justify-end">
                            {(inkfrogOrdersRecentImportsLimit ?? 10) === 10 ? (
                              <button
                                type="button"
                                className="tw-text-xs tw-font-medium tw-text-blue-600 hover:tw-underline"
                                onClick={() => dispatch(expandInkfrogOrdersRecentImports())}
                              >
                                Show more
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="tw-text-xs tw-font-medium tw-text-blue-600 hover:tw-underline"
                                onClick={() => dispatch(collapseInkfrogOrdersRecentImports())}
                              >
                                Show less
                              </button>
                            )}
                          </div>
                        ) : null}
                        {typeof inkfrogOrderImportsRetentionNote === 'string' &&
                        inkfrogOrderImportsRetentionNote.trim() !== '' ? (
                          <p className="tw-mt-2 tw-text-xs tw-text-slate-500">{inkfrogOrderImportsRetentionNote}</p>
                        ) : null}
                      </div>
                    </div>
                  ) : channelId === 'payhelm_ai_mcp' ? (
                    <McpKeyDisplay
                      ppKeys={ppKeys}
                      ownerId={ownerId}
                      sharedAccountIds={sharedAccountIds}
                      canDisconnectAllTeamIntegrations={canDisconnectAllTeamIntegrations}
                    />
                  ) : channelEnabled && !pkeyTableEnabled ? (
                    <ChannelAccountTable
                      accounts={channelAccounts}
                      isAdmin={isAdmin}
                      integration={integration}
                      ownerId={ownerId}
                      retrieveAccounts={retrieveAccounts}
                      ppKeys={ppKeys}
                      canDisconnectAllTeamIntegrations={canDisconnectAllTeamIntegrations}
                      allowDisconnectOwnConnections={
                        Boolean(currentUserEmail) && !canDisconnectAllTeamIntegrations
                      }
                      currentUserEmail={currentUserEmail}
                      backfillEnabled={backfillEnabled}
                      onBackfillForAccount={onBackfillForAccount}
                    />
                  ) : (
                    type === 'feed' ? (
                      <FeedConfigTable integration={integration}/>
                    ) : (
                      <ChannelKeysTable
                        ppKeys={ppKeys}
                        integration={integration}
                        ownerId={ownerId}
                        canDisconnectAllTeamIntegrations={canDisconnectAllTeamIntegrations}
                        currentUserId={user?.id}
                      />
                    )
                  )}
                </div>
              ) : (
                description
              )}
            </div>
          </div>
          {expandButtonVisible && (
            <div
              className="tw-mt-2 tw-shrink-0 tw-h-[24px] tw-cursor-pointer tw-flex tw-justify-center tw-p-1 tw-rounded-lg tw-bg-slate-100 hover:tw-bg-slate-200 tw-transition-all"
              onClick={handleExpand}
            >
              {expanded ? (
                <div className="tw-flex tw-items-center tw-space-x-1 tw-text-slate-600">
                  <div className="tw-text-xs">Show less</div>
                  <FiChevronUp />
                </div>
              ) : (
                <div className="tw-flex tw-items-center tw-space-x-1 tw-text-slate-600">
                  <div className="tw-text-xs">Show more</div>
                  <FiChevronDown />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
